import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Mail, Phone, Target } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  ProgressBar,
} from '@tutor/ui';
import { ApiError } from '../../lib/api-client';
import { useStudent } from '../../hooks/queries';
import { LessonCard } from '../../components/LessonCard';
import { formatDate, formatScore, humaniseEnum } from '../../lib/format';

const MASTERY_TONE = {
  NOT_STARTED: 'neutral',
  EMERGING: 'danger',
  DEVELOPING: 'warning',
  SECURE: 'success',
  MASTERED: 'brand',
} as const;

export function StudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const { data, isLoading, error } = useStudent(studentId);

  if (isLoading) return <LoadingState />;

  if (error) {
    const apiError = error instanceof ApiError ? error : null;
    return (
      <ErrorState
        title={apiError?.isForbidden ? 'That student is not on your roster' : 'Student not found'}
        description={apiError?.message}
        action={
          <Link to="/tutor/students">
            <Button variant="outline">Back to students</Button>
          </Link>
        }
      />
    );
  }

  if (!data) return null;

  return (
    <>
      <Link
        to="/tutor/students"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All students
      </Link>

      <div className="mb-6 flex flex-wrap items-start gap-5">
        <Avatar name={data.user.fullName} src={data.user.avatarUrl} size="xl" />
        <div className="min-w-0 flex-1">
          <PageHeader
            className="mb-0"
            title={data.user.fullName}
            description={
              <span className="flex flex-wrap items-center gap-2">
                <span>{data.profile?.yearGroup ?? '—'}</span>
                {data.profile?.school && (
                  <>
                    <span aria-hidden>·</span>
                    <span>{data.profile.school}</span>
                  </>
                )}
                {data.subjects.map((subject) => (
                  <Badge key={subject.id} tone="brand">
                    {subject.name}
                  </Badge>
                ))}
              </span>
            }
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Progress by subject" />
            <CardBody className="space-y-5">
              {data.progress.length === 0 ? (
                <EmptyState title="No progress recorded yet" icon={<Target className="h-5 w-5" aria-hidden />} />
              ) : (
                data.progress.map((subject) => (
                  <div key={subject.id}>
                    <ProgressBar label={subject.subject.name} value={subject.overallPercentage} />
                    <dl className="mt-2 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                      <div>
                        <dt className="text-slate-500">Trend</dt>
                        <dd className="font-medium text-slate-900">{humaniseEnum(subject.trend)}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Target</dt>
                        <dd className="font-medium text-slate-900">{subject.targetGrade ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Predicted</dt>
                        <dd className="font-medium text-slate-900">{subject.predictedGrade ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-slate-500">Homework</dt>
                        <dd className="font-medium text-slate-900">{subject.homeworkCompletionRate}%</dd>
                      </div>
                    </dl>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Topic mastery" />
            <CardBody>
              {data.topics.length === 0 ? (
                <EmptyState title="No topics tracked yet" />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.topics.map((topic) => (
                    <li key={topic.id} className="flex items-center gap-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-900">{topic.topicName}</p>
                        <p className="truncate text-xs text-slate-500">{topic.courseName}</p>
                      </div>
                      <div className="w-24">
                        <ProgressBar value={topic.confidence} size="sm" showValue={false} />
                      </div>
                      <Badge tone={MASTERY_TONE[topic.masteryLevel]}>{humaniseEnum(topic.masteryLevel)}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Recent homework" />
            <CardBody>
              {data.recentHomework.length === 0 ? (
                <EmptyState title="No homework set yet" />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.recentHomework.map((item) => (
                    <li key={item.id}>
                      <Link to={`/tutor/homework/${item.id}`} className="flex items-center gap-3 py-2.5 hover:bg-slate-50">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-slate-900">{item.title}</p>
                          <p className="text-xs text-slate-500">Due {formatDate(item.dueAt)}</p>
                        </div>
                        <Badge tone={item.status === 'MARKED' ? 'success' : item.status === 'OVERDUE' ? 'danger' : 'neutral'}>
                          {humaniseEnum(item.status)}
                        </Badge>
                        {item.score !== null && (
                          <span className="w-14 text-right text-sm font-medium tabular-nums text-slate-900">
                            {formatScore(item.score, item.maxScore)}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Contact" />
            <CardBody className="space-y-2.5 text-sm">
              <p className="flex items-center gap-2 text-slate-700">
                <Mail className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                <span className="truncate">{data.user.email}</span>
              </p>
              {data.profile?.guardianName && (
                <>
                  <p className="pt-2 text-xs font-medium uppercase tracking-wide text-slate-500">Guardian</p>
                  <p className="text-slate-900">{data.profile.guardianName}</p>
                  {data.profile.guardianEmail && (
                    <p className="flex items-center gap-2 text-slate-700">
                      <Mail className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                      <span className="truncate">{data.profile.guardianEmail}</span>
                    </p>
                  )}
                  {data.profile.guardianPhone && (
                    <p className="flex items-center gap-2 text-slate-700">
                      <Phone className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                      {data.profile.guardianPhone}
                    </p>
                  )}
                </>
              )}
              {data.profile?.targetGrade && (
                <p className="flex items-center gap-2 pt-2 text-slate-700">
                  <GraduationCap className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  Target grade {data.profile.targetGrade}
                </p>
              )}
            </CardBody>
          </Card>

          {data.profile?.notes && (
            <Card>
              <CardHeader title="Notes" />
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{data.profile.notes}</p>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Upcoming lessons" />
            <CardBody className="space-y-3">
              {data.upcomingLessons.length === 0 ? (
                <EmptyState title="Nothing scheduled" />
              ) : (
                data.upcomingLessons.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} perspective="tutor" showDay />
                ))
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Assessments" />
            <CardBody>
              {data.assessments.length === 0 ? (
                <EmptyState title="No assessments yet" />
              ) : (
                <ul className="space-y-2.5">
                  {data.assessments.map((assessment) => (
                    <li key={assessment.id} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-slate-900">{assessment.title}</p>
                        {assessment.score !== null && (
                          <Badge tone="success">{formatScore(assessment.score, assessment.maxScore)}</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {humaniseEnum(assessment.type)} · {formatDate(assessment.assessedAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
